import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  SpeakerWaveIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { accuracyPercent, firstDefined } from '../lib/practiceAttempts';
import {
  buildIeltsRecordingUrl,
  formatIeltsBand,
  IELTS_DIMENSION_LABELS,
  IELTS_DIMENSIONS,
  normalizeIeltsAssessment,
} from '../lib/ieltsSpeaking';

const identity = (value) => value;

const asArray = (...values) => values.find(Array.isArray) || [];

export const normalizeOutcomeItems = (details = {}) => asArray(
  details.items,
  details.item_results,
  details.attempts,
  details.outcomes,
  details.results,
  details.answers,
);

const answerText = (item) => firstDefined(
  item.answer,
  item.response,
  item.user_answer,
  item.userAnswer,
  item.spelling,
  item.typed,
  '',
);

const meaningText = (item) => firstDefined(
  item.meaning,
  item.definition,
  item.definition_cn,
  item.definition_en,
  item.correct_answer,
  '',
);

const itemLabel = (item, index) => item.word || item.display_label || item.item_key || `Item ${index + 1}`;

const safeCount = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const Metric = ({ label, value, t = identity }) => (
  <div className="border-r border-gray-200 px-3 py-3 last:border-r-0">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(label)}</div>
    <div className="mt-1 text-lg font-bold text-gray-900" data-i18n-skip>{value}</div>
  </div>
);

const Metrics = ({ values, t }) => (
  <div className="mt-4 grid grid-cols-2 border-y border-gray-200 sm:grid-cols-4">
    {values.map(([label, value]) => <Metric key={label} label={label} value={value} t={t} />)}
  </div>
);

const OutcomeStatus = ({ passed, t }) => (
  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${passed ? 'text-emerald-700' : 'text-rose-700'}`}>
    {passed ? <CheckCircleIcon className="h-4 w-4" aria-hidden="true" /> : <XCircleIcon className="h-4 w-4" aria-hidden="true" />}
    {t(passed ? 'Passed' : 'Needs retry')}
  </span>
);

const OutcomeRows = ({ items, renderMeta, t }) => {
  if (!items.length) return <p className="mt-5 text-sm text-gray-500">{t('No item outcomes were saved.')}</p>;
  return (
    <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
      {items.map((item, index) => {
        const passed = Boolean(firstDefined(item.passed, item.correct, item.is_correct, false));
        return (
          <div key={item.item_key || item.id || `${itemLabel(item, index)}-${index}`} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="font-semibold text-gray-900" data-i18n-skip>{itemLabel(item, index)}</div>
              {meaningText(item) && <div className="mt-1 text-sm text-gray-600" data-i18n-skip>{meaningText(item)}</div>}
              {renderMeta?.(item, index)}
            </div>
            <OutcomeStatus passed={passed} t={t} />
          </div>
        );
      })}
    </div>
  );
};

export function DictationResultAdapter({ attempt = {}, details = {}, t = identity }) {
  const items = normalizeOutcomeItems(details);
  const metrics = attempt.metrics || {};
  const firstPass = firstDefined(details.first_pass_accuracy, metrics.first_pass_accuracy, attempt.accuracy);
  const retries = safeCount(firstDefined(details.retry_count, details.retries, metrics.retries, metrics.errors), 0);
  return (
    <section aria-label={t('Dictation result')}>
      <h2 className="text-lg font-bold text-gray-900">{t('Spelling and meaning outcomes')}</h2>
      <Metrics
        t={t}
        values={[
          ['First-pass accuracy', accuracyPercent(firstPass) == null ? t('Not available') : `${accuracyPercent(firstPass)}%`],
          ['Retry items', retries],
          ['Rounds', safeCount(firstDefined(details.rounds, metrics.rounds), 0)],
          ['Items', items.length || safeCount(details.total_items, 0)],
        ]}
      />
      <OutcomeRows
        items={items}
        t={t}
        renderMeta={(item) => (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            {answerText(item) && <span><span className="font-semibold">{t('Answer')}:</span> <span data-i18n-skip>{answerText(item)}</span></span>}
            {safeCount(firstDefined(item.attempt_count, item.attempts), 0) > 0 && <span>{t('{count} tries', { count: safeCount(firstDefined(item.attempt_count, item.attempts), 0) })}</span>}
          </div>
        )}
      />
    </section>
  );
}

export function MeaningResultAdapter({ attempt = {}, details = {}, t = identity }) {
  const items = normalizeOutcomeItems(details);
  const metrics = attempt.metrics || {};
  const average = firstDefined(details.average_response_ms, metrics.average_response_ms);
  return (
    <section aria-label={t('Meaning result')}>
      <h2 className="text-lg font-bold text-gray-900">{t('Meaning answers')}</h2>
      <Metrics
        t={t}
        values={[
          ['Accuracy', accuracyPercent(firstDefined(details.accuracy, attempt.accuracy)) == null ? t('Not available') : `${accuracyPercent(firstDefined(details.accuracy, attempt.accuracy))}%`],
          ['Average response', average == null ? t('Not available') : `${Math.round(Number(average) / 100) / 10}s`],
          ['Timeouts', safeCount(firstDefined(details.timeouts, metrics.timeouts), 0)],
          ['Items', items.length || safeCount(details.total, 0)],
        ]}
      />
      <OutcomeRows
        items={items}
        t={t}
        renderMeta={(item) => {
          const responseMs = firstDefined(item.response_ms, item.elapsed_ms, item.time_ms);
          return (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              {answerText(item) && <span><span className="font-semibold">{t('Answer')}:</span> <span data-i18n-skip>{answerText(item)}</span></span>}
              {responseMs != null && <span>{t('Response time')}: {Math.round(Number(responseMs))}{t('ms')}</span>}
            </div>
          );
        }}
      />
    </section>
  );
}

export function LightningResultAdapter({ attempt = {}, details = {}, t = identity }) {
  const metrics = attempt.metrics || {};
  const rounds = asArray(details.rounds, details.round_results);
  const items = normalizeOutcomeItems(details);
  const rows = rounds.length ? rounds : items;
  return (
    <section aria-label={t('Lightning result')}>
      <h2 className="text-lg font-bold text-gray-900">{t('Round and item outcomes')}</h2>
      <Metrics
        t={t}
        values={[
          ['Accuracy', accuracyPercent(firstDefined(details.accuracy, attempt.accuracy, metrics.accuracy)) == null ? t('Not available') : `${accuracyPercent(firstDefined(details.accuracy, attempt.accuracy, metrics.accuracy))}%`],
          ['Best streak', safeCount(firstDefined(details.best_streak, metrics.best_streak), 0)],
          ['Rounds', rounds.length || safeCount(firstDefined(details.round_count, metrics.rounds), 0)],
          ['Items', items.length || safeCount(details.total, 0)],
        ]}
      />
      <OutcomeRows
        items={rows}
        t={t}
        renderMeta={(item, index) => (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>{rounds.length ? `${t('Round')} ${safeCount(firstDefined(item.round, item.round_no), index + 1)}` : `${t('Item')} ${index + 1}`}</span>
            {firstDefined(item.response_ms, item.elapsed_ms) != null && <span>{t('Response time')}: {Math.round(Number(firstDefined(item.response_ms, item.elapsed_ms)))}{t('ms')}</span>}
          </div>
        )}
      />
    </section>
  );
}

const recordingUrl = (recording = {}) => firstDefined(
  recording.playback_url,
  recording.recording_url,
  recording.audio_url,
  recording.url,
);

const standardAudioUrl = (item) => firstDefined(
  item.standard_audio_url,
  item.standardAudioUrl,
  item.reference_audio_url,
  item.tts_url,
);

export const groupPronunciationItems = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const passedOn = firstDefined(item.passed_on_attempt, item.passedOnAttempt);
    const key = item.passed || passedOn != null ? String(passedOn || 1) : 'not_passed';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return Array.from(groups.entries()).sort(([left], [right]) => {
    if (left === 'not_passed') return 1;
    if (right === 'not_passed') return -1;
    return Number(left) - Number(right);
  });
};

const AudioControl = ({ url, label, t }) => {
  if (!url) return null;
  return (
    <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
      <SpeakerWaveIcon className="h-4 w-4" aria-hidden="true" />
      <span>{t(label)}</span>
      <audio controls preload="none" src={url} aria-label={t(label)} className="h-8 max-w-full" />
    </div>
  );
};

const PronunciationItem = ({ item, index, t }) => {
  const recordings = asArray(item.recordings, item.recording_attempts, item.attempts);
  const passing = recordings.find((recording) => recording.passed) || recordings[recordings.length - 1];
  const earlierFailed = recordings.filter((recording) => recording !== passing && !recording.passed);
  const passScore = firstDefined(item.score, passing?.score, passing?.overall_score);
  return (
    <div className="border-b border-gray-200 py-4 last:border-b-0">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="font-semibold text-gray-900" data-i18n-skip>{itemLabel(item, index)}</div>
          {item.sentence && <div className="mt-1 text-sm text-gray-600" data-i18n-skip>{item.sentence}</div>}
        </div>
        <OutcomeStatus passed={Boolean(item.passed)} t={t} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
        {passScore != null && <span><span className="font-semibold">{t('Score')}:</span> <span data-i18n-skip>{passScore}</span></span>}
        {item.feedback && <span data-i18n-skip>{item.feedback}</span>}
      </div>
      <AudioControl url={standardAudioUrl(item)} label="Standard audio" t={t} />
      <AudioControl url={recordingUrl(passing)} label="Student recording" t={t} />
      {earlierFailed.length > 0 && (
        <details className="mt-3 border-l-2 border-gray-200 pl-3">
          <summary className="cursor-pointer text-sm font-semibold text-gray-700">{t('Earlier failed recordings')}</summary>
          <div className="mt-2 space-y-2">
            {earlierFailed.map((recording, recordingIndex) => (
              <div key={recording.id || recording.recording_id || recordingIndex} className="text-sm text-gray-600">
                <span>{t('Attempt')} {safeCount(firstDefined(recording.attempt_number, recording.attempt_no), recordingIndex + 1)}</span>
                {recording.score != null && <span> · {t('Score')}: <span data-i18n-skip>{recording.score}</span></span>}
                <AudioControl url={recordingUrl(recording)} label="Failed recording" t={t} />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export function PronunciationResultAdapter({ attempt = {}, details = {}, t = identity }) {
  const items = normalizeOutcomeItems(details);
  const groups = groupPronunciationItems(items);
  const metrics = attempt.metrics || {};
  return (
    <section aria-label={t('Pronunciation result')}>
      <h2 className="text-lg font-bold text-gray-900">{t('Pronunciation outcomes')}</h2>
      <Metrics
        t={t}
        values={[
          ['Accuracy', accuracyPercent(firstDefined(details.accuracy, attempt.accuracy, metrics.accuracy)) == null ? t('Not available') : `${accuracyPercent(firstDefined(details.accuracy, attempt.accuracy, metrics.accuracy))}%`],
          ['Passed items', safeCount(firstDefined(details.passed_count, metrics.passed_count), items.filter((item) => item.passed).length)],
          ['Total items', safeCount(firstDefined(details.total_items, metrics.total_items), items.length)],
          ['Pass groups', groups.filter(([key]) => key !== 'not_passed').length],
        ]}
      />
      {!groups.length ? (
        <p className="mt-5 text-sm text-gray-500">{t('No pronunciation item outcomes were saved.')}</p>
      ) : (
        <div className="mt-5 divide-y divide-gray-200 border-y border-gray-200">
          {groups.map(([key, group]) => (
            <div key={key}>
              <div className="flex items-center gap-2 py-3 text-sm font-semibold text-gray-800">
                <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                {key === 'not_passed' ? t('Not passed') : t('Passed on attempt {count}', { count: key })}
                <span className="text-gray-500">({group.length})</span>
              </div>
              <div className="pl-5">
                {group.map((item, index) => <PronunciationItem key={item.item_key || item.id || index} item={item} index={index} t={t} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const questionAnswer = (question) => firstDefined(
  question.response,
  question.user_answer,
  question.answer_given,
  question.answer,
  '',
);

export function AdaptiveReadingResultAdapter({ attempt = {}, details = {}, t = identity }) {
  const reading = details.reading || {};
  const analysis = details.analysis || details.ai_review || details.review || {};
  const questions = asArray(details.questions, reading.questions);
  const summary = firstDefined(reading.summary, details.summary, analysis.summary, '');
  return (
    <section aria-label={t('Adaptive reading result')}>
      <h2 className="text-lg font-bold text-gray-900">{t('Reading summary')}</h2>
      {summary && <p className="mt-3 leading-7 text-gray-800" data-i18n-skip>{summary}</p>}
      <Metrics
        t={t}
        values={[
          ['Accuracy', accuracyPercent(firstDefined(details.accuracy, attempt.accuracy)) == null ? t('Not available') : `${accuracyPercent(firstDefined(details.accuracy, attempt.accuracy))}%`],
          ['Correct', safeCount(firstDefined(details.correct, attempt.metrics?.correct), questions.filter((question) => question.correct).length)],
          ['Questions', questions.length || safeCount(firstDefined(details.total, attempt.metrics?.total), 0)],
          ['AI review', analysis.summary ? t('Available') : t('Not available')],
        ]}
      />
      {analysis.summary && (
        <div className="mt-5 border-l-2 border-teal-500 pl-4">
          <div className="flex items-center gap-2 font-semibold text-teal-800"><ClockIcon className="h-4 w-4" aria-hidden="true" />{t('AI coaching review')}</div>
          <p className="mt-2 text-sm leading-6 text-gray-700" data-i18n-skip>{analysis.summary}</p>
          {Array.isArray(analysis.next_steps) && analysis.next_steps.length > 0 && <p className="mt-2 text-sm text-gray-700" data-i18n-skip>{analysis.next_steps.join(' ')}</p>}
        </div>
      )}
      <h3 className="mt-6 text-base font-bold text-gray-900">{t('Question review')}</h3>
      {!questions.length ? (
        <p className="mt-3 text-sm text-gray-500">{t('No reading questions were saved.')}</p>
      ) : (
        <div className="mt-3 divide-y divide-gray-200 border-y border-gray-200">
          {questions.map((question, index) => (
            <div key={question.id || index} className="py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="font-semibold text-gray-900"><span>{index + 1}. </span><span data-i18n-skip>{question.prompt || question.question || ''}</span></div>
                <OutcomeStatus passed={Boolean(question.correct)} t={t} />
              </div>
              <div className="mt-2 space-y-1 text-sm text-gray-700">
                <p><span className="font-semibold">{t('Your answer')}:</span> <span data-i18n-skip>{questionAnswer(question) || t('No answer')}</span></p>
                {question.answer && <p><span className="font-semibold">{t('Correct answer')}:</span> <span data-i18n-skip>{question.answer}</span></p>}
                {question.explanation && <p><span className="font-semibold">{t('Explanation')}:</span> <span data-i18n-skip>{question.explanation}</span></p>}
                {question.evidence && <p><span className="font-semibold">{t('Evidence')}:</span> <span data-i18n-skip>{question.evidence}</span></p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export const RecordingPlayback = ({ url, label = 'Play my recording', t = identity }) => {
  const [playbackUrl, setPlaybackUrl] = useState(() => (
    url && !String(url).startsWith('/api/') ? url : ''
  ));
  const [loading, setLoading] = useState(Boolean(url && String(url).startsWith('/api/')));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    const source = String(url || '').trim();
    setFailed(false);
    if (!source) {
      setPlaybackUrl('');
      setLoading(false);
      return () => {};
    }
    if (!source.startsWith('/api/')) {
      setPlaybackUrl(source);
      setLoading(false);
      return () => {};
    }

    setPlaybackUrl('');
    setLoading(true);
    fetch(source, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        const blob = await response.blob();
        if (!blob.size) throw new Error();
        objectUrl = URL.createObjectURL(blob);
        if (active) setPlaybackUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!url || failed) return <p className="mt-4 text-sm text-gray-500">{t('The recording is not available for playback.')}</p>;
  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900">
        <SpeakerWaveIcon className="h-4 w-4 text-indigo-600" aria-hidden="true" />
        {t('My recording')}
      </h3>
      {loading ? (
        <p className="mt-3 text-sm text-gray-500">{t('Loading recording...')}</p>
      ) : (
        <audio className="mt-3 w-full" controls preload="metadata" src={playbackUrl} aria-label={t(label)}>
          {t('Your browser cannot play this recording.')}
        </audio>
      )}
    </div>
  );
};

export function IeltsSpeakingResultAdapter({ attempt = {}, details = {}, t = identity, viewer = 'student' }) {
  const assessment = normalizeIeltsAssessment({ ...attempt, ...details });
  const dimensions = assessment.dimensions || {};
  const question = details.question || details.config?.question || attempt.question || '';
  const part = details.part || details.config?.part || attempt.part || '';
  const scope = details.assessment_scope || details.config?.assessment_scope || attempt.assessment_scope || (
    question || part ? 'prompt' : 'general'
  );
  const band = assessment.predicted_overall == null
    ? t('Not available')
    : formatIeltsBand(assessment.predicted_overall);
  const recordingUrl = assessment.recording_url || buildIeltsRecordingUrl({
    recordingId: assessment.recording_id || details.recording_id || attempt.recording_id,
    viewer,
    studentId: attempt.student_id || details.student_id,
  });
  return (
    <section aria-label={t('IELTS speaking result')}>
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('IELTS speaking assessment')}</h2>
        </div>
        <div className="w-fit border border-indigo-200 bg-indigo-50 px-5 py-3 text-center">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Predicted band')}</div>
          <div className="mt-1 text-3xl font-bold tabular-nums text-indigo-800" data-i18n-skip>{band}</div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 border border-gray-200 sm:grid-cols-4">
        {IELTS_DIMENSIONS.map((key) => (
          <div key={key} className="border-b border-gray-200 p-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(IELTS_DIMENSION_LABELS[key])}</div>
            <div className="mt-1 text-xl font-bold tabular-nums text-gray-900" data-i18n-skip>
              {dimensions[key] == null ? t('Not available') : formatIeltsBand(dimensions[key])}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 border-y border-gray-200 py-4">
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
          <span><span className="font-semibold text-gray-800">{t('Assessment scope')}:</span> {t(scope === 'general' ? 'General speaking assessment' : 'Prompt-based assessment')}</span>
          {part && <span><span className="font-semibold text-gray-800">{t('Part')}:</span> <span data-i18n-skip>{`Part ${part}`}</span></span>}
        </div>
        {question ? (
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-800" data-i18n-skip>{question}</p>
        ) : (
          <p className="mt-3 text-sm text-gray-600">{t(scope === 'general' ? 'General speaking assessment' : 'No prompt supplied; speak about a topic you choose.')}</p>
        )}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="border-l-2 border-indigo-500 pl-4">
          <h3 className="font-bold text-gray-900">{t('Why this band')}</h3>
          <p className="mt-2 text-sm leading-6 text-gray-700" data-i18n-skip>{assessment.rationale || t('No written rationale was returned.')}</p>
        </div>
        <div className="border-l-2 border-emerald-500 pl-4">
          <h3 className="font-bold text-gray-900">{t('Coach feedback')}</h3>
          <dl className="mt-2 space-y-3 text-sm leading-6 text-gray-700">
            <div><dt className="font-semibold text-gray-900">{t('Progress')}</dt><dd data-i18n-skip>{assessment.progress || t('Keep building a clear, complete answer.')}</dd></div>
            <div><dt className="font-semibold text-gray-900">{t('Priority issue')}</dt><dd data-i18n-skip>{assessment.priority_issue || t('No priority issue was returned.')}</dd></div>
          </dl>
        </div>
      </div>

      <div className="mt-5 border border-gray-200 bg-gray-50 p-4">
        <h3 className="font-bold text-gray-900">{t('Next steps')}</h3>
        {assessment.next_steps.length > 0 ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-gray-700">
            {assessment.next_steps.map((step, index) => <li key={`${step}-${index}`} data-i18n-skip>{step}</li>)}
          </ul>
        ) : <p className="mt-2 text-sm text-gray-700">{t('Repeat the answer once with one more supporting detail.')}</p>}
        <p className="mt-3 text-sm leading-6 text-gray-800"><span className="font-semibold">{t('Practice action')}:</span> <span data-i18n-skip>{assessment.exercise_action || t('Record the same answer again and focus on the priority issue.')}</span></p>
      </div>

      <RecordingPlayback url={recordingUrl} t={t} />
    </section>
  );
}

const UnsupportedResultAdapter = ({ t = identity }) => (
  <p className="text-sm text-gray-500">{t('This result type is not available yet.')}</p>
);

export const PRACTICE_RESULT_ADAPTERS = Object.freeze({
  dictation: DictationResultAdapter,
  meaning_sprint: MeaningResultAdapter,
  sudden_sprint: LightningResultAdapter,
  pronunciation: PronunciationResultAdapter,
  adaptive_reading: AdaptiveReadingResultAdapter,
  ielts_speaking: IeltsSpeakingResultAdapter,
});

export const getPracticeResultAdapter = (practiceType) => (
  PRACTICE_RESULT_ADAPTERS[practiceType] || UnsupportedResultAdapter
);

export default function PracticeResultAdapter({ practiceType, attempt, details, t = identity }) {
  const Adapter = getPracticeResultAdapter(practiceType);
  return <Adapter attempt={attempt} details={details} t={t} />;
}
